import { useMemo } from 'react';
import { Tree } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import { AiOutlineFile, AiOutlineFolder } from 'react-icons/ai';
import type { GroupFileTree, GroupFileTreeFolder, GroupReference } from '../../../../../services/GroupManager/groupManager.service';
import styles from './filePermissionsPanel.module.scss';

export interface FilePermissionsPanelProps {
  tree: GroupFileTree;
  selectedFolderIds: number[];
  selectedFileIds: number[];
  onChange: (selection: { folderIds: number[]; fileIds: number[] }) => void;
}

// Folder and file ids come from separate tables, so tree keys are prefixed to stay unique.
const FOLDER_KEY_PREFIX = 'folder-';
const FILE_KEY_PREFIX = 'file-';

interface TreeIndex {
  parentOf: Map<string, string | null>;
  descendantsOf: Map<string, string[]>;
}

// Parent and descendant keys for every node, so selections can be expanded and trimmed.
const indexTree = (tree: GroupFileTree): TreeIndex => {
  const parentOf = new Map<string, string | null>();
  const descendantsOf = new Map<string, string[]>();

  const visitFile = (file: GroupReference, parentKey: string | null) => {
    const key = `${FILE_KEY_PREFIX}${file.id}`;
    parentOf.set(key, parentKey);
    return [key];
  };
  const visitFolder = (folder: GroupFileTreeFolder, parentKey: string | null): string[] => {
    const key = `${FOLDER_KEY_PREFIX}${folder.id}`;
    parentOf.set(key, parentKey);
    const descendants = [...folder.folders.flatMap((child) => visitFolder(child, key)), ...folder.files.flatMap((file) => visitFile(file, key))];
    descendantsOf.set(key, descendants);
    return [key, ...descendants];
  };

  tree.folders.forEach((folder) => visitFolder(folder, null));
  tree.files.forEach((file) => visitFile(file, null));
  return { parentOf, descendantsOf };
};

const ancestorsOf = (key: string, parentOf: TreeIndex['parentOf']) => {
  const ancestors: string[] = [];
  for (let current = parentOf.get(key) ?? null; current; current = parentOf.get(current) ?? null) ancestors.push(current);
  return ancestors;
};

const idsWithPrefix = (keys: string[], prefix: string) => keys.filter((key) => key.startsWith(prefix)).map((key) => Number(key.slice(prefix.length)));

// A folder permission covers everything inside it, so the selection only holds
// the folders and files actually granted. Their contents show as checked but
// locked (covered by the folder), and folders with something granted inside
// show as partially checked. Checks only flow down: checking every child
// never grants the parent, which would also cover anything added to it later.
const FilePermissionsPanel = ({ tree, selectedFolderIds, selectedFileIds, onChange }: FilePermissionsPanelProps) => {
  const index = useMemo(() => indexTree(tree), [tree]);
  const selectedKeys = useMemo(
    () => [...selectedFolderIds.map((id) => `${FOLDER_KEY_PREFIX}${id}`), ...selectedFileIds.map((id) => `${FILE_KEY_PREFIX}${id}`)],
    [selectedFolderIds, selectedFileIds],
  );

  const { coveredKeys, partialKeys } = useMemo(() => {
    const covered = new Set(selectedKeys.flatMap((key) => index.descendantsOf.get(key) ?? []));
    const partial = new Set(selectedKeys.flatMap((key) => ancestorsOf(key, index.parentOf)).filter((key) => !covered.has(key)));
    return { coveredKeys: covered, partialKeys: partial };
  }, [selectedKeys, index]);

  const treeData = useMemo(() => {
    const toFileNode = (file: GroupReference): TreeDataNode => {
      const key = `${FILE_KEY_PREFIX}${file.id}`;
      return { key, title: file.name, icon: <AiOutlineFile />, isLeaf: true, disableCheckbox: coveredKeys.has(key) };
    };
    const toFolderNode = (folder: GroupFileTreeFolder): TreeDataNode => {
      const key = `${FOLDER_KEY_PREFIX}${folder.id}`;
      return {
        key,
        title: folder.name,
        icon: <AiOutlineFolder />,
        disableCheckbox: coveredKeys.has(key),
        children: [...folder.folders.map(toFolderNode), ...folder.files.map(toFileNode)],
      };
    };
    return [...tree.folders.map(toFolderNode), ...tree.files.map(toFileNode)];
  }, [tree, coveredKeys]);

  const handleCheck: TreeProps['onCheck'] = (_checked, { node, checked }) => {
    const key = String(node.key);
    const descendants = new Set(index.descendantsOf.get(key) ?? []);
    // Checking a folder replaces any grants inside it, which it now covers.
    const nextKeys = checked ? [...selectedKeys.filter((selected) => !descendants.has(selected)), key] : selectedKeys.filter((selected) => selected !== key);
    onChange({ folderIds: idsWithPrefix(nextKeys, FOLDER_KEY_PREFIX), fileIds: idsWithPrefix(nextKeys, FILE_KEY_PREFIX) });
  };

  if (treeData.length === 0) return <p className={styles.empty}>No files or folders found</p>;

  return (
    <div className={styles.panel}>
      <Tree
        className={styles.tree}
        treeData={treeData}
        checkable
        checkStrictly
        selectable={false}
        showIcon
        blockNode
        checkedKeys={{ checked: [...selectedKeys, ...coveredKeys], halfChecked: [...partialKeys] }}
        onCheck={handleCheck}
      />
    </div>
  );
};

export default FilePermissionsPanel;
